import { Box, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { useEffect, useMemo, useRef, useState } from "react";
import type { TankData } from "../models/WeeklyIncentiveData";
import { getZoneColor, SURFACE, TEXT } from "../constants/colors";
import { useIncThresholds } from "../hooks/useIncThresholds";
import { IncPercentPill } from "./IncPercentPill";

type Props = TankData;

export const IncentiveTank = ({
	label,
	sublabel,
	finished,
	target,
	incPercent,
}: Props) => {
	const { thresholds } = useIncThresholds();
	const containerRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(0);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver(([entry]) => {
			setHeight(entry.contentRect.height);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const noData = incPercent == null;
	const pct = incPercent ?? 0;
	const zoneColor = getZoneColor(noData ? null : pct, thresholds);
	const fillColor = zoneColor.bg;

	const maxVal = Math.max(finished, target, 1);
	const targetColor = "#42A5F5";

	const chartOptions = useMemo<EChartsOption>(
		() => ({
			tooltip: {
				show: true,
				formatter: () =>
					`Finished Tons: ${finished.toFixed(0)}<br/>Target Tons: ${target.toFixed(0)}<br/>Incentive: ${pct.toFixed(1)}%`,
				borderColor: fillColor,
				appendTo: document.body,
			},
			grid: {
				left: 5,
				right: 5,
				top: 20,
				bottom: 5,
				containLabel: false,
			},
			xAxis: {
				type: "category" as const,
				data: ["Finished", "Target"],
				show: false,
			},
			yAxis: {
				type: "value" as const,
				min: 0,
				max: maxVal,
				show: false,
			},
			series: [
				{
					type: "bar",
					data: [
						{
							value: noData ? 0 : finished,
							itemStyle: {
								color: fillColor,
								borderRadius: [4, 4, 0, 0],
							},
						},
						{
							value: target,
							itemStyle: {
								color: targetColor,
								borderRadius: [4, 4, 0, 0],
							},
						},
					],
					barWidth: "65%",
					barMinHeight: 4,
					label: {
						show: true,
						position: "top",
						formatter: (params: { dataIndex?: number }) => {
							if (params.dataIndex === 0) return `${finished.toFixed(0)}`;
							return `${target.toFixed(0)}`;
						},
						fontSize: 12,
						fontWeight: 700,
						color: TEXT.secondary,
					},
					silent: false,
				},
			],
		}),
		[finished, target, fillColor, noData, pct, maxVal],
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				flex: 1,
				minWidth: 0,
			}}
		>
			<Typography
				sx={{
					fontSize: "0.7rem",
					fontWeight: 600,
					color: TEXT.primary,
					lineHeight: 1.2,
					textAlign: "center",
				}}
			>
				{label}
			</Typography>

			<Box
				ref={containerRef}
				sx={{
					width: "100%",
					flex: 1,
					minHeight: 0,
					border: `1px solid ${SURFACE.borderLight}`,
					borderRadius: 1,
					backgroundColor: SURFACE.background,
					overflow: "hidden",
				}}
			>
				{height > 0 && (
					<ReactECharts
						option={chartOptions}
						notMerge
						style={{ height, width: "100%" }}
						opts={{ renderer: "svg" }}
					/>
				)}
			</Box>

			<Typography
				sx={{
					fontSize: "0.6rem",
					color: TEXT.tertiary,
					lineHeight: 1.2,
					mt: 0.75,
				}}
			>
				{sublabel}
			</Typography>
			<Box sx={{ mt: 0.5 }}>
				<IncPercentPill
					value={noData ? 0 : incPercent}
					thresholds={thresholds}
					isFuture={noData}
				/>
			</Box>
		</Box>
	);
};
